import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "bg-white px-8 pt-2 pb-2" };
const _hoisted_2 = { class: "text-lg font-semibold text-gray-900 mb-4 flex items-center" };
const _hoisted_3 = { class: "ml-auto flex items-center" };
const _hoisted_4 = {
    key: 0,
    class: "text-center py-8"
};
const _hoisted_5 = { class: "space-y-3" };
const _hoisted_6 = { class: "flex items-center" };
const _hoisted_7 = ["checked"];
const _hoisted_8 = { class: "flex items-center justify-between flex-1 gap-3" };
const _hoisted_9 = { class: "flex items-center gap-3 flex-1 min-w-0" };
const _hoisted_10 = { class: "flex-1 min-w-0" };
const _hoisted_11 = { class: "text-sm text-gray-600 truncate" };
const _hoisted_12 = { class: "flex items-center gap-2 flex-shrink-0" };
const _hoisted_13 = { class: "hidden md:flex items-center gap-1" };
const _hoisted_14 = { class: "flex items-center" };
const _hoisted_15 = ["checked"];
const _hoisted_16 = { class: "flex items-center gap-3 flex-1 min-w-0" };
const _hoisted_17 = { class: "flex-1 min-w-0" };
const _hoisted_18 = { class: "font-medium text-gray-900 block" };
const _hoisted_19 = { class: "text-sm text-gray-600 truncate" };
const _hoisted_20 = {
    key: 2,
    class: "mt-4 p-3 bg-gray-100 rounded text-xs"
};
const _hoisted_21 = { class: "mt-2" };
const _hoisted_22 = { class: "mt-2" };
import { computed, watch } from "vue";
import { useCheckoutStore } from "../../stores/checkout-store";
import { useTheme } from '@shared/composables/useTheme';
export default /*@__PURE__*/ _defineComponent({
    __name: 'DynamicPaymentMethodSelection',
    props: {
        modelValue: {},
        showDebug: { type: Boolean, default: false },
        deliveryType: {}
    },
    emits: ["update:modelValue", "method-selected"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // ✅ Use Checkout Store for Payment Methods
        const checkoutStore = useCheckoutStore();
        const { theme } = useTheme();
        // ✅ Direct store access - no need for computed wrappers
        // const loading = computed(() => checkoutStore.paymentMethodsLoading)
        // const paymentConfig = computed(() => checkoutStore.paymentMethods)
        // const selectedMethod = computed({
        //   get: () => checkoutStore.selectedPaymentMethod,
        //   set: (value) => checkoutStore.selectPaymentMethod(value)
        // })
        const paymentMethods = computed(() => checkoutStore.paymentMethods);
        const isManualFallback = computed(() => {
            if (!checkoutStore.paymentMethods)
                return true;
            // Manual ist Fallback wenn es nicht explizit enabled ist, aber auch keine andere Methode verfügbar ist
            const hasEnabledPayPalSources = checkoutStore.paymentMethods.paypal.enabled &&
                Object.values(checkoutStore.paymentMethods.paypal.fundingSources || {}).some((enabled) => enabled === true);
            const manualExplicitlyEnabled = checkoutStore.paymentMethods.manual.enabled;
            return !manualExplicitlyEnabled && !hasEnabledPayPalSources;
        });
        const isOnlinePaymentAvailable = computed(() => {
            return Boolean(checkoutStore.paymentMethods?.onlinePaymentAvailable);
        });
        const onlineDisplayLabel = computed(() => {
            return checkoutStore.paymentMethods?.displayLabel || 'Jetzt online bezahlen';
        });
        const onlineProviderHint = computed(() => {
            const provider = checkoutStore.paymentMethods?.activeOnlineProvider;
            if (provider === 'stripe')
                return 'Sicher bezahlen mit Stripe (inkl. Karten, Wallets, Klarna, SEPA)';
            if (provider === 'paypal')
                return 'Sicher bezahlen mit PayPal';
            return 'Sicher online bezahlen';
        });
        /**
         * Icon + Hintergrund passend zum aktiven Online-Provider.
         * - PayPal: PayPal Brand-Icon (FontAwesome Brands)
         * - Stripe: generisches Credit-Card-Icon
         * - Fallback: Globe (Online-Zahlung)
         */
        const onlineIconClass = computed(() => {
            const provider = checkoutStore.paymentMethods?.activeOnlineProvider;
            if (provider === 'paypal')
                return 'fab fa-paypal text-blue-600';
            if (provider === 'stripe')
                return 'fas fa-credit-card text-indigo-600';
            return 'fas fa-globe text-blue-600';
        });
        const onlineIconBackground = computed(() => {
            const provider = checkoutStore.paymentMethods?.activeOnlineProvider;
            if (provider === 'stripe')
                return 'bg-indigo-50';
            return 'bg-blue-50';
        });
        const hasAvailableMethods = computed(() => {
            if (!checkoutStore.paymentMethods)
                return false;
            return (checkoutStore.paymentMethods.manual.enabled ||
                checkoutStore.paymentMethods.onlinePaymentAvailable ||
                checkoutStore.paymentMethods.paypal.enabled ||
                isManualFallback.value); // Fallback ist immer verfügbar
        });
        // ✅ Removed onMounted - payment config comes from store now
        // ✅ Watcher für modelValue Änderungen
        watch(() => props.modelValue, (newValue) => {
            if (newValue && newValue !== checkoutStore.selectedPaymentMethod) {
                checkoutStore.selectPaymentMethod(newValue);
            }
        }, { immediate: true });
        // ✅ Watcher für Auto-Selection nach Config-Load
        watch([() => checkoutStore.paymentMethods, hasAvailableMethods], () => {
            // Auto-select Manual Payment wenn noch nichts ausgewählt wurde
            if (!checkoutStore.selectedPaymentMethod && hasAvailableMethods.value && checkoutStore.paymentMethods) {
                // ✅ Priorisiere "Zahlung vor Ort" als Standard
                if (checkoutStore.paymentMethods.manual.enabled || checkoutStore.paymentMethods.manual.isDefault) {
                    selectMethod("manual");
                }
                else if (checkoutStore.paymentMethods.onlinePaymentAvailable) {
                    selectMethod("online");
                }
                else if (checkoutStore.paymentMethods.paypal.enabled) {
                    selectMethod("paypal");
                }
                else if (isManualFallback.value) {
                    selectMethod("manual");
                }
            }
        });
        const selectMethod = (method) => {
            checkoutStore.selectPaymentMethod(method);
            emit("update:modelValue", method);
            emit("method-selected", method, checkoutStore.paymentMethods);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                _createElementVNode("h3", _hoisted_2, [
                    _cache[4] || (_cache[4] = _createTextVNode(" Zahlungsmethode ", -1 /* CACHED */)),
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", {
                            class: _normalizeClass([
                                'w-2 h-2 rounded-full',
                                _unref(checkoutStore).selectedPaymentMethod ? 'bg-green-500' : 'bg-gray-400'
                            ])
                        }, null, 2 /* CLASS */)
                    ])
                ]),
                _createCommentVNode(" Loading State "),
                (_unref(checkoutStore).paymentMethodsLoading)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[5] || (_cache[5] = [
                            _createElementVNode("div", { class: "inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-green-600" }, null, -1 /* CACHED */),
                            _createElementVNode("p", { class: "mt-2 text-gray-600 text-sm" }, "Zahlmethoden werden geladen...", -1 /* CACHED */)
                        ]))]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createCommentVNode(" Payment Methods "),
                        _createElementVNode("div", _hoisted_5, [
                            _createCommentVNode(" Manual Payment (Standard) "),
                            (_unref(checkoutStore).paymentMethods?.manual.enabled || isManualFallback.value)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: _normalizeClass(["payment-option cursor-pointer p-3 rounded-lg border transition-all", {
                                            'border-green-300 bg-green-50': _unref(checkoutStore).selectedPaymentMethod === 'manual',
                                            'border-gray-200 hover:border-gray-300 hover:bg-gray-50': _unref(checkoutStore).selectedPaymentMethod !== 'manual',
                                        }]),
                                    onClick: _cache[1] || (_cache[1] = ($event) => (selectMethod('manual')))
                                }, [
                                    _createElementVNode("div", _hoisted_6, [
                                        _createElementVNode("input", {
                                            type: "radio",
                                            checked: _unref(checkoutStore).selectedPaymentMethod === 'manual',
                                            class: "w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500 mr-3",
                                            onChange: _cache[0] || (_cache[0] = ($event) => (selectMethod('manual')))
                                        }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_7),
                                        _createElementVNode("div", _hoisted_8, [
                                            _createCommentVNode(" Left: Icon + Text "),
                                            _createElementVNode("div", _hoisted_9, [
                                                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "hidden md:flex w-8 h-8 bg-green-100 rounded-full items-center justify-center flex-shrink-0" }, [
                                                    _createElementVNode("i", { class: "fas fa-money-bill text-green-600 text-sm" })
                                                ], -1 /* CACHED */)),
                                                _createElementVNode("div", _hoisted_10, [
                                                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "font-medium text-gray-900 block" }, "Manuelle Zahlung", -1 /* CACHED */)),
                                                    _createElementVNode("div", _hoisted_11, " Bar" + _toDisplayString(_unref(checkoutStore).getCardPaymentText()) + " bei " + _toDisplayString(props.deliveryType === "pickup" ? "Abholung" : "Lieferung"), 1 /* TEXT */)
                                                ])
                                            ]),
                                            _createCommentVNode(" Right cluster: Badge + Payment Icons (vertikal zentriert auf Card-Höhe) "),
                                            _createElementVNode("div", _hoisted_12, [
                                                _cache[9] || (_cache[9] = _createElementVNode("span", { class: "px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full" }, " Empfohlen ", -1 /* CACHED */)),
                                                _createElementVNode("div", _hoisted_13, [
                                                    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "w-6 h-4 bg-gray-600 rounded text-white text-xs flex items-center justify-center font-bold" }, " € ", -1 /* CACHED */)),
                                                    (_unref(checkoutStore).getCardPaymentText())
                                                        ? (_openBlock(), _createElementBlock("div", {
                                                            key: 0,
                                                            class: "w-6 h-4 rounded text-white text-xs flex items-center justify-center font-bold",
                                                            style: _normalizeStyle({ backgroundColor: _unref(theme).colors.primary })
                                                        }, " EC ", 4 /* STYLE */))
                                                        : _createCommentVNode("v-if", true)
                                                ])
                                            ])
                                        ])
                                    ])
                                ], 2 /* CLASS */))
                                : _createCommentVNode("v-if", true),
                            _createCommentVNode(" Online Payment (Stripe/PayPal resolver) "),
                            (isOnlinePaymentAvailable.value)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 1,
                                    class: _normalizeClass(["payment-option cursor-pointer p-3 rounded-lg border transition-all", {
                                            'border-green-300 bg-green-50': _unref(checkoutStore).selectedPaymentMethod === 'online',
                                            'border-gray-200 hover:border-gray-300 hover:bg-gray-50': _unref(checkoutStore).selectedPaymentMethod !== 'online',
                                        }]),
                                    onClick: _cache[3] || (_cache[3] = ($event) => (selectMethod('online')))
                                }, [
                                    _createElementVNode("div", _hoisted_14, [
                                        _createElementVNode("input", {
                                            type: "radio",
                                            checked: _unref(checkoutStore).selectedPaymentMethod === 'online',
                                            class: "w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500 mr-3",
                                            onChange: _cache[2] || (_cache[2] = ($event) => (selectMethod('online')))
                                        }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_15),
                                        _createElementVNode("div", _hoisted_16, [
                                            _createElementVNode("div", {
                                                class: _normalizeClass(["hidden md:flex w-8 h-8 rounded-full items-center justify-center flex-shrink-0", onlineIconBackground.value])
                                            }, [
                                                _createElementVNode("i", {
                                                    class: _normalizeClass([onlineIconClass.value, "text-sm"])
                                                }, null, 2 /* CLASS */)
                                            ], 2 /* CLASS */),
                                            _createElementVNode("div", _hoisted_17, [
                                                _createElementVNode("span", _hoisted_18, _toDisplayString(onlineDisplayLabel.value), 1 /* TEXT */),
                                                _createElementVNode("div", _hoisted_19, _toDisplayString(onlineProviderHint.value), 1 /* TEXT */)
                                            ])
                                        ])
                                    ])
                                ], 2 /* CLASS */))
                                : _createCommentVNode("v-if", true)
                        ])
                    ], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */)),
                _createCommentVNode(" Payment Configuration Debug (Development) "),
                (_ctx.showDebug && _unref(checkoutStore).paymentMethods)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                        _createElementVNode("details", null, [
                            _cache[19] || (_cache[19] = _createElementVNode("summary", { class: "cursor-pointer font-medium" }, "Payment Config Debug", -1 /* CACHED */)),
                            _createElementVNode("pre", _hoisted_21, _toDisplayString(JSON.stringify(_unref(checkoutStore).paymentMethods, null, 2)), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_22, [
                                _createElementVNode("div", null, [
                                    _cache[10] || (_cache[10] = _createElementVNode("strong", null, "Store Payment Methods:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(JSON.stringify(paymentMethods.value, null, 2)), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[11] || (_cache[11] = _createElementVNode("strong", null, "paypal.enabled:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(paymentMethods.value?.paypal?.enabled), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[12] || (_cache[12] = _createElementVNode("strong", null, "manual.enabled:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(paymentMethods.value?.manual?.enabled), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[13] || (_cache[13] = _createElementVNode("strong", null, "cardByPickup.enabled:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(paymentMethods.value?.cardByPickup?.enabled), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[14] || (_cache[14] = _createElementVNode("strong", null, "cardByDelivery.enabled:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(paymentMethods.value?.cardByDelivery?.enabled), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[15] || (_cache[15] = _createElementVNode("strong", null, "isManualFallback:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(isManualFallback.value), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[16] || (_cache[16] = _createElementVNode("strong", null, "deliveryType:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(props.deliveryType), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[17] || (_cache[17] = _createElementVNode("strong", null, "checkoutStore.orderType:", -1 /* CACHED */)),
                                    _createTextVNode(" " + _toDisplayString(_unref(checkoutStore).orderType), 1 /* TEXT */)
                                ]),
                                _createElementVNode("div", null, [
                                    _cache[18] || (_cache[18] = _createElementVNode("strong", null, "cardText:", -1 /* CACHED */)),
                                    _createTextVNode(" \"" + _toDisplayString(_unref(checkoutStore).getCardPaymentText()) + "\"", 1 /* TEXT */)
                                ])
                            ])
                        ])
                    ]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
